import { Suspense, useState, useCallback } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import Container from '@/components/Container';
import PageHeader from '@/components/PageHeader';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateFaq from '@/components/DefaultContentTemplate/Faq';
import {
  QUERY_FETCH_RESOURCE_HUB_PAGE,
  type FetchResourceHubPageQuery,
  type ComponentCommonFaqComponent,
  ResourceDefaultContentTemplateDynamicZone,
} from '@edwin-group/people-graphql';

import { useResourceHubDetail } from './hooks/useResourceHubDetail';
import { useAssetPreview } from './hooks/useAssetPreview';
import { VersionHistory } from './components/version-history';
import { PreviewModal } from './components/preview-modal';
import { ResourceHubDetailLoading } from './components/loading';
import type { ResourceAssetItem } from '../shared/types';
import { Tag } from '@/components/Tag';
import BookmarkButton from '@/components/Bookmark/BookmarkButton';
import { Enum_Bookmark_Category } from '@edwin-group/people-graphql';
import { BackButton } from '@/components/ui/back-button';
import Divider from '@/components/Divider';

const ResourceHubDetailPageContent = () => {
  const { data: resourceHubPageData } = useSuspenseQuery<FetchResourceHubPageQuery>(QUERY_FETCH_RESOURCE_HUB_PAGE);
  const { resource } = useResourceHubDetail();
  const { isPreviewOpen, previewAsset, previewAssets, currentAssetIndex, openPreview, closePreview, switchAsset } =
    useAssetPreview();
  const [assetPreviewStatuses, setAssetPreviewStatuses] = useState<Record<string, ResourceAssetItem['previewStatus']>>(
    {},
  );

  const resourceHubPage = resourceHubPageData?.resourceHubPage;
  const howToUseThisTitle = resourceHubPage?.howToUseThisTitle ?? 'How to use this';
  const downloadButtonText = resourceHubPage?.downloadButtonText ?? 'Download';
  const previewButtonText = resourceHubPage?.previewButtonText ?? 'Preview';
  const downloadAllButtonText = resourceHubPage?.downloadAllButtonText ?? 'Download all files';

  const handlePreviewStatusChange = useCallback((documentId: string, previewStatus: 'unavailable') => {
    setAssetPreviewStatuses((prev) => ({
      ...prev,
      [documentId]: previewStatus,
    }));
  }, []);

  if (!resource) return null;

  const defaultContent = resource.DefaultContentTemplate ?? [];
  const contentWithoutFaq = defaultContent.filter(
    (c): c is ResourceDefaultContentTemplateDynamicZone => c != null && c?.__typename !== 'ComponentCommonFaqComponent',
  );
  const faqBlocks = defaultContent.filter(
    (c): c is ComponentCommonFaqComponent => c != null && c?.__typename === 'ComponentCommonFaqComponent',
  );

  return (
    <>
      <PageHeader
        bookmarkButton={
          <BookmarkButton
            relationId={resource.documentId ?? ''}
            metadata={resource as Parameters<typeof BookmarkButton>[0]['metadata']}
            typeId={resource.documentId ?? ''}
            category={Enum_Bookmark_Category.Resource}
          />
        }
        backButton={<BackButton to="/resource-hub" label="Back to resource hub" />}
        tag={<Tag label={resource.type?.type ?? 'Resource Type'} textColor="text-black-200" />}
        title={resource.title}
      />
      <Container>
        <div className="col-span-12 mt-[0px] md:mt-[64px]">
          <Divider className="block md:hidden" />
          {resource.subHeader && (
            <>
              <h2 className="mb-[32px] font-haffer text-sh-24 font-bold">{howToUseThisTitle}</h2>
              <p className="mb-sh-15 font-haffer text-sh-16-24 text-gray-700">{resource.subHeader}</p>
            </>
          )}
        </div>
        <div className="col-span-12">
          <DefaultContentTemplate
            content={contentWithoutFaq}
            resourceHub={{
              resource: {
                documentId: resource.documentId,
                title: resource.title,
                type: resource.type,
                category: resource.category,
                resourceHistory: resource.resourceHistory,
              },
              onPreview: openPreview,
              assetPreviewStatuses,
              downloadButtonText,
              previewButtonText,
              downloadAllButtonText,
            }}
          />

          <div className="mb-sh-60">
            <VersionHistory versions={resource.resourceHistory} />
          </div>

          {faqBlocks.map((faq) => (
            <DefaultContentTemplateFaq key={faq.id} component={faq} isDefaultContentTemplate />
          ))}
        </div>
      </Container>
      <PreviewModal
        resource={resource}
        asset={previewAsset}
        assets={previewAssets}
        currentAssetIndex={currentAssetIndex}
        onAssetChange={switchAsset}
        isOpen={isPreviewOpen}
        onClose={closePreview}
        onPreviewStatusChange={handlePreviewStatusChange}
        downloadButtonText={downloadButtonText}
      />
    </>
  );
};

export const ResourceHubDetailPage = () => {
  return (
    <Suspense fallback={<ResourceHubDetailLoading />}>
      <ResourceHubDetailPageContent />
    </Suspense>
  );
};
